/**
 * Utility for rendering skeleton tiles based on rail type
 *
 * @remarks
 * This utility provides a consistent way to render loading skeleton tiles
 * across different pages and components. It automatically determines the
 * correct tile type based on rail metadata.
 *
 * @example
 * ```tsx
 * // Render default skeleton tiles
 * {renderSkeletonTiles(undefined, 6)}
 *
 * // Render skeleton tiles based on rail metadata
 * {renderSkeletonTiles(railMetadata, 8)}
 * ```
 */

import { ReactElement } from "react";

import { Tile34 } from "@/components/molecules/Tile34";
import { SecondaryNavTile } from "@/components/molecules/SecondaryNavTile";
import { MiniGameTile } from "@/components/molecules/MiniGameTile";
import { RailWithTiles, isGameRail } from "@/utils/FetchBrowsePage/types";

/**
 * Check if a rail should render SecondaryNavTiles
 * @param rail - The rail metadata to check
 * @returns True if the rail should use SecondaryNavTiles
 */
const isSecondaryNavRail = (rail: RailWithTiles): boolean =>
  rail.title.toLowerCase().includes("brand");

/**
 * Render skeleton tiles based on rail template type
 *
 * @param railMetadata - Optional rail metadata to determine tile type
 * @param count - Number of skeleton tiles to render (default: 6)
 * @returns Array of skeleton tile components
 *
 * @example
 * ```tsx
 * // Default skeleton tiles
 * const skeletons = renderSkeletonTiles();
 *
 * // Game rail skeletons
 * const gameSkeletons = renderSkeletonTiles(gameRailMetadata, 8);
 *
 * // Brand rail skeletons
 * const brandSkeletons = renderSkeletonTiles(brandRailMetadata, 4);
 * ```
 */
export const renderSkeletonTiles = (
  railMetadata?: RailWithTiles,
  count: number = 6,
): ReactElement[] => {
  if (!railMetadata) {
    return Array.from({ length: count }).map((_, tileIndex) => (
      <Tile34 key={`skeleton-tile-${tileIndex}`} loading />
    ));
  }

  if (isSecondaryNavRail(railMetadata)) {
    return Array.from({ length: count }).map((_, tileIndex) => (
      <SecondaryNavTile key={`skeleton-tile-${tileIndex}`} loading />
    ));
  }

  if (isGameRail(railMetadata)) {
    return Array.from({ length: count }).map((_, tileIndex) => (
      <MiniGameTile key={`skeleton-tile-${tileIndex}`} loading />
    ));
  }

  return Array.from({ length: count }).map((_, tileIndex) => (
    <Tile34 key={`skeleton-tile-${tileIndex}`} loading />
  ));
};
